import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_ORIGIN } from "../../config/siteSeo";
import {
    getHomeStoryRowPublishedAtMs,
    getLocalizedStoryFromHomeStoryRo,
    resolveHomeStoryBlob,
    truncateText,
} from "../browse-stories/storyRoUtils";

const DEFAULT_TITLE = "Story on Puzzle Interact";
const DEFAULT_DESCRIPTION = "Read the story behind this puzzle on Puzzle Interact.";
const DESCRIPTION_MAX_CHARS = 155;

/** Prefer a 1200-wide preview, then the full image, then the 400px icon. */
function resolveOgImage(row) {
    const story = resolveHomeStoryBlob(row);
    if (!story || typeof story !== "object") return "";
    const candidates = [story.imagePreviewUrl, story.imageUrl, story.imageIcon400Url];
    for (const c of candidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
    }
    return "";
}

function toIsoDateTime(ms) {
    if (ms == null || !Number.isFinite(ms)) return undefined;
    try {
        return new Date(ms).toISOString();
    } catch {
        return undefined;
    }
}

/**
 * Per-story title / description / OG tags / JSON-LD for the indexable `/read-story/:storyId` page.
 * Canonical + robots are also emitted globally by {@link SeoHead}.
 * While the story is loading, do not emit the generic title — every UUID would look identical to Google.
 */
export default function ReadStorySeoHead({
    storyId,
    row = null,
    contentLanguage,
    loading = false,
    error = null,
}) {
    const sid = typeof storyId === "string" ? storyId.trim() : "";
    if (!sid) return null;

    const url = `${SITE_ORIGIN}/read-story/${encodeURIComponent(sid)}`;
    if (loading && !row) {
        return (
            <Helmet>
                <link rel="canonical" href={url} />
                <meta name="robots" content="index, follow" />
            </Helmet>
        );
    }
    if (error || !row) {
        return (
            <Helmet>
                <title>Story not found | Puzzle Interact</title>
                <link rel="canonical" href={url} />
                <meta name="robots" content="noindex, follow" />
            </Helmet>
        );
    }

    const localized = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
    const title = (localized?.title || "").trim() || DEFAULT_TITLE;
    const fullTitle = `${title} | Puzzle Interact`;
    const description =
        truncateText((localized?.text || "").trim(), DESCRIPTION_MAX_CHARS) || DEFAULT_DESCRIPTION;
    const imageUrl = resolveOgImage(row);
    const inLanguage = String(contentLanguage || "").toLowerCase() === "french" ? "fr" : "en";
    const datePublished = toIsoDateTime(row ? getHomeStoryRowPublishedAtMs(row) : null);
    const articleLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description,
        inLanguage,
        mainEntityOfPage: url,
        url,
        publisher: {
            "@type": "Organization",
            name: "Jeux Puzzle Interact",
            url: SITE_ORIGIN,
        },
    };
    if (imageUrl) articleLd.image = imageUrl;
    if (datePublished) articleLd.datePublished = datePublished;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
            {datePublished ? <meta property="article:published_time" content={datePublished} /> : null}
            <meta property="og:locale" content={inLanguage === "fr" ? "fr_CA" : "en_CA"} />
            <meta property="og:site_name" content="Puzzle Interact" />
            <meta name="twitter:card" content={imageUrl ? "summary_large_image" : "summary"} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}
            <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        </Helmet>
    );
}
